import {
  Box,
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import SnackbarMessage from '../SnackbarMessage';
import Validations, { MaxLengthValidationFunc } from '../../utils/Validations';
import pageStyle from '../../theme/styles/pages/FormPage';
import ChannelBannerImageUploader from './ChannelBannerImageUploader';
import { channelsStatus } from '../../config/Constants';
import { createChannel, getChannelNumbers, updateChannel } from '../../services/Channels';
import AppLoader from '../AppLoader';

const useStyles = makeStyles(pageStyle);

const ActivateChannel = ({ channel, onSuccess }) => {
  const classes = useStyles();

  const [processing, setProcessing] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [banner, setBanner] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [numbers, setNumbers] = useState([]);
  const [snackbarMeesage, setSnackbarMeesage] = useState({
    show: false,
    type: '',
    message: '',
  });

  const { handleSubmit, control, getValues, watch, setValue } = useForm();

  useEffect(() => {
    if (!channel || (channel && channel.status === channelsStatus.REJECTED)) {
      getChannelNumbers()
        .then((res) => {
          const alreadyTakenNumbers = res.data;
          const availableNumbers = Array.from({ length: 999 - 101 + 1 }, (_, i) => i + 101).filter(
            (num) => !alreadyTakenNumbers.includes(num) || (channel && channel.number === num)
          );
          setNumbers(availableNumbers);
          if (!channel?.number) {
            setValue('channelNumber', availableNumbers?.[0]);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    if (channel?.id) {
      setValue('channelNumber', channel.number);
      setValue('channelName', channel.name);
      setValue('channelHeadline', channel.headline);
      setBanner(channel.bannerImage);
    }
  }, []);

  const handleThumbnailUpload = (url) => {
    setBanner(url);
  };

  const submitData = (data) => {
    setSnackbarMeesage({
      message: '',
      type: '',
      show: false,
    });
    if (!banner) {
      setSnackbarMeesage({
        message: 'Please upload a banner image.',
        type: 'error',
        show: true,
      });
      return;
    }

    const payload = {
      number: data.channelNumber,
      name: data.channelName,
      headline: data.channelHeadline,
      bannerImage: banner,
    };
    if (channel?.id) {
      updateChannel(channel?.id, payload)
        .then(() => {
          setProcessing(false);
          // handleDialog();
          onSuccess();
        })
        .catch(({ response }) => {
          setSnackbarMeesage({
            ...snackbarMeesage,
            message: response.data.message,
            type: 'error',
            show: true,
          });
          setProcessing(false);
        });
    } else {
      createChannel(payload)
        .then(() => {
          setProcessing(false);
          // handleDialog();
          onSuccess();
        })
        .catch(({ response }) => {
          setSnackbarMeesage({
            ...snackbarMeesage,
            message: response.data.message,
            type: 'error',
            show: true,
          });
          setProcessing(false);
        });
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short', // "Jul"
      day: '2-digit', // "29"
    });
    return formattedDate;
  };

  const defaultFormValues = getValues();

  return (
    <>
      {loading && <AppLoader height="50vh" />}
      {channel && channel.status === channelsStatus.INACTIVE && (
        <Box mt={2}>
          <Typography variant="body2" gutterBottom>
            Your channel has been deactivated.
          </Typography>
          <Typography variant="body2">
            If you think this is a mistake, send us a message via the Contact Us button on the left
            side bar.
          </Typography>
        </Box>
      )}
      {channel && channel.status === channelsStatus.PENDING && (
        <Box mt={2}>
          <Typography variant="body2" gutterBottom>
            Your request for channel activation has been submitted on {formatDate(channel.modified)}
            . Please check back within 5 business days for an update.
          </Typography>
          <Typography variant="body2">
            If it has been more than 5 business days, you may follow up via the Contact Us button on
            the left side bar.
          </Typography>
        </Box>
      )}
      {!loading && (!channel || (channel && channel.status === channelsStatus.REJECTED)) && (
        <>
          {channel && channel.status === channelsStatus.REJECTED && (
            <Grid item xs={12} md={6}>
              <Box mt={2}>
                <Typography variant="body2">
                  Your channel activation request has been rejected for the following reason:
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 100,
                    backgroundColor: '#f9f9f9',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {channel.rejectionNote}
                  </Typography>
                </Box>

                <Typography variant="body2" mt={1}>
                  Please edit your request and submit again.
                </Typography>
              </Box>
            </Grid>
          )}

          <Typography component="h3" variant="h6" style={{ marginTop: 4, marginBottom: 8 }}>
            Activate Your Channel
          </Typography>
          <Grid container spacing={2}>
            {/* <DialogTitle>{dialogTitle}</DialogTitle> */}
            <Grid item xs={12} md={6}>
              <form id="add-perk" onSubmit={handleSubmit(submitData)}>
                <Controller
                  control={control}
                  rules={Validations.REQUIRED}
                  name="channelNumber"
                  id="channelNumber"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      variant="outlined"
                      label="Channel Number"
                      value={value || numbers?.[0]}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      className={classes.formInput}
                      SelectProps={{
                        native: true,
                      }}
                      fullWidth
                      select
                    >
                      {numbers.map((number) => (
                        <option key={number} value={number}>
                          {number}
                        </option>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  rules={{
                    ...Validations.REQUIRED,
                    ...MaxLengthValidationFunc(55),
                  }}
                  name="channelName"
                  id="channelName"
                  defaultValue={defaultFormValues.channelName || ''}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      variant="outlined"
                      label="Channel Name"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      className={classes.formInput}
                      fullWidth
                      autoFocus
                    />
                  )}
                />
                <div style={{ color: 'grey', marginTop: '-10px', marginBottom: '10px' }}>
                  Your Channel Name must incorporate your own name or company. Subject for approval.
                </div>
                <Controller
                  control={control}
                  rules={{
                    ...MaxLengthValidationFunc(300),
                  }}
                  name="channelHeadline"
                  id="channelHeadline"
                  defaultValue={defaultFormValues.channelHeadline || ''}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      label="Channel Headline (Optional)"
                      multiline
                      rows={4}
                      value={value}
                      onChange={onChange}
                      variant="outlined"
                      fullWidth
                      inputProps={{ maxLength: 300 }}
                      error={!!error}
                      helperText={
                        error?.message || (
                          <span
                            style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
                          >
                            {`${value?.length || 0}/300`}
                          </span>
                        )
                      }
                    />
                  )}
                />
                <div style={{ color: 'grey', marginTop: '-10px', marginBottom: '10px' }}>
                  Say something about you and your content in 300 characters or less
                </div>

                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <ChannelBannerImageUploader
                    key="banner-uploader"
                    onSuccess={handleThumbnailUpload}
                    setUploading={setUploading}
                    title="Banner"
                  />
                  {banner && (
                    <Button
                      size="small"
                      variant="text"
                      color="black"
                      style={{ textDecoration: 'underline' }}
                      onClick={() => setShowImagePreview(true)}
                      disabled={!banner}
                    >
                      Generate Banner Preview
                    </Button>
                  )}
                </div>

                {showImagePreview && banner && (
                  <Box flexDirection="column" mt={2}>
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '100%',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid #ccc',
                      }}
                    >
                      <img
                        src={banner}
                        alt="Banner Preview"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          height: '100%',
                          width: '100%',
                          background:
                            'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.2) 60%)',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          padding: '20px',
                          boxSizing: 'border-box',
                          zIndex: 100,
                        }}
                      >
                        <div
                          style={{
                            color: 'white',
                            textAlign: 'center',
                            textShadow: '1px 1px 4px black',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 'bold' }}>
                            {watch('channelName')}
                          </div>
                          <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
                            {watch('channelHeadline')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Box>
                )}
              </form>
              <Grid item xs={12} md={12}>
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    type="submit"
                    endIcon={processing && <CircularProgress size={25} />}
                    disabled={processing || uploading}
                    form="add-perk"
                  >
                    Submit
                  </Button>
                </Box>
              </Grid>
            </Grid>
            {snackbarMeesage.show && <SnackbarMessage {...snackbarMeesage} />}
          </Grid>
        </>
      )}
    </>
  );
};

export default ActivateChannel;

ActivateChannel.propTypes = {
  channel: PropTypes.objectOf.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
